import React from 'react';
import { Button } from '@/components/ui/button';
import { useQuickscan } from '@/contexts/QuickscanContext';
import { Building, Factory, ShoppingBag, Truck, ArrowRight } from 'lucide-react';

const businessTypes = [
  { id: 'office', label: 'Kantoor', icon: Building, description: 'Kantoorgebouw of administratieve diensten' },
  { id: 'manufacturing', label: 'Productie', icon: Factory, description: 'Fabricage en industriële processen' },
  { id: 'retail', label: 'Retail', icon: ShoppingBag, description: 'Winkels en commerciële ruimtes' },
  { id: 'logistics', label: 'Logistiek', icon: Truck, description: 'Transport, opslag en distributie' }
];

const employeeCounts = [
  { value: '1-10', label: '1-10 werknemers' },
  { value: '11-50', label: '11-50 werknemers' },
  { value: '51-200', label: '51-200 werknemers' },
  { value: '200+', label: '200+ werknemers' }
];

export const BusinessTypeScreen = () => {
  const { data, updateData, nextStep, prevStep } = useQuickscan();

  const handleBusinessTypeSelect = (type: string) => {
    updateData('businessType', type);
  };

  const handleEmployeeCountSelect = (count: string) => {
    updateData('employeeCount', count);
  };

  const canProceed = data.businessType && data.employeeCount;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-energy-green mb-2">
          Vertel ons over jouw bedrijf
        </h2>
        <p className="text-gray-600">
          Dit helpt ons om je energiegebruik beter te begrijpen
        </p>
      </div>

      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Wat beschrijft jouw bedrijf het beste?</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {businessTypes.map((type) => {
            const Icon = type.icon;
            const isSelected = data.businessType === type.id;
            
            return (
              <div
                key={type.id}
                onClick={() => handleBusinessTypeSelect(type.id)}
                className={`p-6 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                  isSelected 
                    ? 'border-energy-green bg-energy-green/5 shadow-md animate-glow' 
                    : 'border-gray-200 hover:border-energy-green/50 hover:shadow-sm'
                }`}
              >
                <Icon className={`w-8 h-8 mx-auto mb-3 ${
                  isSelected ? 'text-energy-green' : 'text-gray-600'
                }`} />
                <h4 className="font-semibold text-center mb-2">{type.label}</h4>
                <p className="text-sm text-gray-600 text-center">{type.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Hoeveel werknemers heeft jouw bedrijf?</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {employeeCounts.map((count) => {
            const isSelected = data.employeeCount === count.value;
            
            return (
              <button
                key={count.value}
                onClick={() => handleEmployeeCountSelect(count.value)}
                className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                  isSelected 
                    ? 'border-energy-blue bg-energy-blue/5 text-energy-blue font-semibold' 
                    : 'border-gray-200 hover:border-energy-blue/50'
                }`}
              >
                {count.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={prevStep}
          className="border-gray-300"
        >
          Vorige
        </Button>
        
        <Button 
          onClick={nextStep}
          disabled={!canProceed}
          className="bg-[#014336] text-white hover:bg-[#014336]/90 disabled:opacity-50"
        >
          Volgende <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};
